import {
  Card,
  DatePicker,
  Flex,
  Form,
  FormProps,
  Input,
  Modal,
  Spin,
  Typography,
} from 'antd';
import Checkbox from 'antd/es/checkbox/Checkbox';
import { Rule } from 'antd/es/form';
import FormContainer from 'components/FormContainer';
import SelectComponent from 'components/Select';
import RBCountryFilter from 'components/UniversalFilters/RBCountryFilter';
import * as dayjs from 'dayjs';
import useAgronomistList from 'hooks/useAgronomistList';
import useFGLList from 'hooks/useFGLList';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { routes } from 'routes/AllRoutes';
import AddressForm from 'screens/stakeholders/components/AddressForm';
import StakeholderStatus from 'screens/stakeholders/components/StakeholderStatus';
import useFGLById from 'screens/stakeholders/hooks/queries/useFGLById';
import { selectToken, selectUserCountry } from 'store/auth/selectors';
import { StakeholderStatus as StakeholderStatusType } from 'types/common';
import { FarmerGroupPayload } from 'types/request';
import { formProps } from 'utils/constants';
import {
  getRoleFromToken,
  getStakeholderTypeForCountry,
  isMobileScreenSize,
  populateOptions,
} from 'utils/helpers';
import usePaddyBuyerList from '../../../../hooks/usePaddyBuyerList';
import { convertFarmerGroupToForm, TFarmerGroupForm } from '../../helper';
import useFarmerGroupById from '../../hooks/useFarmerGroupByIdQuery';
import useMutateFarmerGroup from '../../hooks/useFarmerGroupMutation';
import './style.css';
import useUserByRoleCountryQuery from 'screens/farmer-groups/hooks/useUserByRoleCountryQuery';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';

const breadcrumbItems = [
  {
    title: (
      <Link className="link" to="/farmer-group">
        Farmer Group Management
      </Link>
    ),
  },
  {
    title: 'Add Individually',
  },
];

const errorStatus = {
  duplicate: {
    status: false,
    message: 'Dates have to be unique from each other',
  },
  unspaced: {
    status: false,
    message: 'Estimated start dates must be at least 90 days apart',
  },
  unordered: {
    status: false,
    message: 'Dates must be in order, from earliest to latest',
  },
};

const FarmerGroupForm = ({ edit = false }: { edit?: boolean }) => {
  // Form States
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isTouched, setIsTouched] = useState(false);
  const [isFullstackParticipant, setIsFullstackParticipant] = useState(false);
  // Filter States
  const [searchPaddyBuyer, setSearchPaddyBuyer] = useState<string>();
  const [searchFGL, setSearchFGL] = useState<string>();
  // Modal States
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmEditOpen, setIsConfirmEditOpen] = useState(false);
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  // Params
  const [searchParams] = useSearchParams();
  const countryParams = searchParams.get('country');
  const leaderIdParams = searchParams.get('leaderId');
  // Variables
  const userCountry = useSelector(selectUserCountry);
  const role = getRoleFromToken(useSelector(selectToken));
  const isSrAgronomist = role === 'TM';
  // Form variables
  const [form] = Form.useForm<TFarmerGroupForm>();
  const country = Form.useWatch('country', form);
  const leaderId = Form.useWatch('leaderId', form);
  const paddyBuyerId = Form.useWatch('paddyBuyerId', form);
  const jrAgronomist = Form.useWatch('juniorAgronomistEmail', form);
  // Fetch / Mutate hooks
  const { data: farmerGroupById, isLoading: isLoadingFetchGroup } =
    useFarmerGroupById(id ?? '', edit);

  const {
    filterOptions: awdAgronomistFilterOptions,
    isLoading: isAWDAgronomistLoading,
  } = useUserByRoleCountryQuery({
    country: [country],
    role: ['Agronomist - AWD'],
  });

  const { data: { data: leaderDataByID } = {} } = useFGLById(leaderId);

  const { paddyBuyerOptions, isLoading: isPaddyBuyerLoading } =
    usePaddyBuyerList(
      {
        search: searchPaddyBuyer,
        country: country,
      },
      !!country,
    );

  const { fglOptions, isLoading: isFGLLoading } = useFGLList(
    {
      size: 20,
      search: searchFGL,
      country: country,
      'groupLeader.specified': false,
    },
    !!country,
  );

  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country, { labelKey: 'email', valueKey: 'email' });

  const mutation = useMutateFarmerGroup({
    onSuccess: () => {
      navigate(routes.farmerGroup);
    },
    onFinish: () => {
      setIsConfirmOpen(false);
    },
    method: edit ? 'PUT' : 'POST',
  });

  /* ------------- Prefill Options From Params and Fetched FG Data ------------- */

  /* Will take FGL data from fetched FG data or URL params */
  const preFilledFGLOption =
    edit && farmerGroupById?.data?.groupLeader?.id
      ? {
          label: farmerGroupById.data.groupLeader?.name,
          value: farmerGroupById.data.groupLeader.id,
          status: farmerGroupById.data.groupLeader?.status,
        }
      : leaderIdParams && country === countryParams
        ? {
            value: leaderDataByID?.id,
            label: leaderDataByID?.name,
            status: leaderDataByID?.status,
          }
        : undefined;

  /* Will take Paddy Buyer data from fetched FG data */
  const preFilledPBOption = farmerGroupById?.data?.paddyBuyer
    ? {
        label: farmerGroupById.data.paddyBuyer?.name,
        value: farmerGroupById.data.paddyBuyer?.id,
        status: farmerGroupById.data.paddyBuyer?.status ?? 'inactive',
      }
    : undefined;

  /* Will populate FGL options with prefilled FGL from URL params or fetched FG data
     and show loading spinner underneath if FGL data is still being fetched */
  const populatedFGLOptions = useMemo(
    () => populateOptions(preFilledFGLOption, fglOptions, isFGLLoading),
    [preFilledFGLOption, fglOptions, isFGLLoading],
  );

  const populatedPBOptions = useMemo(
    () =>
      populateOptions(
        preFilledPBOption,
        paddyBuyerOptions,
        isPaddyBuyerLoading,
      ),
    [preFilledPBOption, paddyBuyerOptions, isPaddyBuyerLoading],
  );

  /* ----------------- Stakeholder Status Related Variables ----------------- */

  const selectedLeaderStatus = useMemo(
    () =>
      populatedFGLOptions
        ?.filter((val) => val.value === leaderId)?.[0]
        ?.status?.toLowerCase() ?? 'inactive',
    [populatedFGLOptions, leaderId],
  );
  const isLeaderActive = selectedLeaderStatus === 'active';

  const selectedPaddyBuyerStatus = useMemo(
    () =>
      populatedPBOptions
        ?.filter((val) => val.value === paddyBuyerId)?.[0]
        ?.status?.toLowerCase() ?? 'inactive',
    [paddyBuyerId, populatedPBOptions],
  );
  const isPaddyBuyerActive = selectedPaddyBuyerStatus === 'active';

  /* ------------------------------------------------------------------------- */

  const validateDatePicker: Rule = ({ getFieldValue }) => ({
    validator: (rule, value) => {
      if (!value) return Promise.resolve();
      // Workaround since 'Rule' type doesn't include 'field' value, but in console log it contains the value.
      // Might need to monitor this if antd version is changed
      const { field: fieldName } = { ...(rule as { field: string }) };
      const dateFieldNames = [
        'firstEstimatedStartDate',
        'secondEstimatedStartDate',
        'thirdEstimatedStartDate',
      ];
      errorStatus.duplicate.status = false;
      errorStatus.unspaced.status = false;
      errorStatus.unordered.status = false;

      const thisIndex = dateFieldNames.indexOf(fieldName);
      const thisDate = dayjs(value).year(dayjs().year());
      dateFieldNames.forEach((field, index) => {
        if (!getFieldValue(field) || field === fieldName) return;
        const fieldDate = dayjs(getFieldValue(field)).year(dayjs().year());
        const dateDifference = thisDate.diff(fieldDate, 'days');
        const isUnique = !thisDate.isSame(fieldDate);
        const isInOrder =
          thisIndex > index ? dateDifference > 0 : dateDifference < 0;
        const isDaysApart =
          (dateDifference > 90 && dateDifference < 275) ||
          (dateDifference < -90 && dateDifference > -275);
        errorStatus.duplicate.status =
          !isUnique || errorStatus.duplicate.status;
        errorStatus.unspaced.status =
          !isDaysApart || errorStatus.unspaced.status;
        errorStatus.unordered.status =
          !isInOrder || errorStatus.unordered.status;
      });

      const error = Object.values(errorStatus).filter((val) => val.status)?.[0]
        ?.message;
      if (!error) return Promise.resolve();
      return Promise.reject(new Error(error));
    },
  });

  const onFieldsChanges = () => {
    if (edit && !isLoadingData) {
      setIsTouched(true);
    }
  };

  const onSubmit: FormProps<TFarmerGroupForm>['onFinish'] = (values) => {
    if (edit) {
      setIsConfirmEditOpen(true);
      return;
    }
    if (values.juniorAgronomistEmail) {
      setIsConfirmOpen(true);
      return;
    }
    submitData();
  };

  const submitData = () => {
    const values = form.getFieldsValue();
    const formatValues: FarmerGroupPayload = {
      ...values,
      id: edit ? farmerGroupById?.data?.id : undefined,
      address: leaderDataByID?.address?.id
        ? { id: leaderDataByID?.address?.id }
        : undefined,
      firstEstimatedStartDate: dayjs(values.firstEstimatedStartDate).format(
        'DD MM',
      ),
      secondEstimatedStartDate: dayjs(values.secondEstimatedStartDate).format(
        'DD MM',
      ),
      thirdEstimatedStartDate: values.thirdEstimatedStartDate
        ? dayjs(values.thirdEstimatedStartDate).format('DD MM')
        : undefined,
      isFullstackParticipant: isFullstackParticipant,
    };

    mutation.mutate(formatValues);
  };

  // Set the country and leaderId from URL params
  useEffect(() => {
    if (countryParams) form.setFieldValue('country', countryParams);
    if (!countryParams) form.setFieldValue('country', userCountry);
    if (leaderIdParams) form.setFieldValue('leaderId', leaderIdParams);
  }, []);

  // Reset fields on country change if not in edit mode
  useEffect(() => {
    if (edit || !country) return;
    form.setFieldValue('juniorAgronomistEmail', null);
    form.setFieldValue('paddyBuyerId', null);
    form.setFieldValue('leaderId', null);
  }, [country]);

  // Convert fetched data to form values
  useEffect(() => {
    if (edit && !!farmerGroupById?.data) {
      const convert = convertFarmerGroupToForm(farmerGroupById?.data);
      form.setFieldsValue(convert);
      setIsFullstackParticipant(!!farmerGroupById?.data?.fullstackParticipant);
      setTimeout(() => setIsLoadingData(false), 1000);
    }
  }, [farmerGroupById?.data]);

  // Set Address from FGL data
  useEffect(() => {
    if (!leaderId) {
      form.setFieldValue('address', null);
      return;
    }
    if (!leaderDataByID) return;
    form.setFieldValue('address', leaderDataByID.address);
    if (!isLeaderActive) form.setFieldValue('juniorAgronomistEmail', null);
  }, [leaderDataByID, isLeaderActive, leaderId]);

  // Enable/Disable Jr Ag field based on Paddy Buyer and FGL status
  useEffect(() => {
    if (!paddyBuyerId) return;
    if (!isPaddyBuyerActive) form.setFieldValue('juniorAgronomistEmail', null);
  }, [paddyBuyerId, isPaddyBuyerActive]);

  return (
    <>
      <Spin spinning={edit && isLoadingFetchGroup} fullscreen />
      <FormContainer
        form={form}
        onFinish={onSubmit}
        onFieldsChange={onFieldsChanges}
        title={
          edit ? t('editFarmerGroupPage.header') : t('addFgForm.addFarmerGroup')
        }
        breadcrumbItems={breadcrumbItems}
        onCancel={() => navigate(routes.farmerGroup)}
        submitBtnProps={{
          disabled: edit && !isTouched,
          loading: mutation.isPending,
        }}
        submitBtnText={
          edit ? t('addFgForm.saveChanges') : t('addFgForm.submitFarmerGroup')
        }
      >
        <Card>
          <Typography.Title level={5} style={{ marginBottom: '24px' }}>
            {t('addFgForm.farmerGroupDetails')}
          </Typography.Title>
          <Form.Item
            name="country"
            label={t('addFgForm.country')}
            required
            rules={[{ required: true }]}
            {...formProps}
          >
            <RBCountryFilter
              label={''}
              placeholder={t('addFgForm.selectCountry')}
              disabled={edit}
              showSearch
              selectedCountry={country}
              onChange={(c) => form.setFieldValue('country', c)}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label={t('addFgForm.farmerGroupName')}
            required
            rules={[{ required: true }]}
            {...formProps}
          >
            <Input
              disabled={edit && isSrAgronomist}
              placeholder={t('addFgForm.inputName')}
            />
          </Form.Item>
          <Form.Item
            name="paddyBuyerId"
            label={getStakeholderTypeForCountry(country, 'PB')}
            required
            rules={[{ required: true }]}
            {...formProps}
          >
            <SelectComponent
              placeholder={t('common.select', {
                value: getStakeholderTypeForCountry(country, 'PB'),
              })}
              showSearch
              onSearch={(value) => setSearchPaddyBuyer(value)}
              options={populatedPBOptions}
              labelRender={({ label }) => (
                <Flex justify="space-between" align="center" gap={8}>
                  <Typography.Text>{label}</Typography.Text>
                  <StakeholderStatus
                    style={{ padding: '0 8px', marginRight: -2 }}
                    status={selectedPaddyBuyerStatus as StakeholderStatusType}
                  />
                </Flex>
              )}
              loading={isPaddyBuyerLoading}
              optionRender={(option) => (
                <Flex justify="space-between" align="center">
                  <Typography.Text>{option?.data?.label}</Typography.Text>
                  {!isPaddyBuyerLoading && (
                    <StakeholderStatus
                      status={option.data?.status}
                      style={{ padding: '0 8px' }}
                    />
                  )}
                </Flex>
              )}
            />
          </Form.Item>
          <Form.Item
            name="leaderId"
            label={t('addFgForm.farmerGroupLeader')}
            required
            rules={[{ required: true }]}
            {...formProps}
          >
            <SelectComponent
              placeholder={t('addFgForm.selectFarmerGroupLeader')}
              showSearch
              onSearch={(value) => setSearchFGL(value)}
              labelRender={({ label }) => (
                <Flex justify="space-between" align="center" gap={8}>
                  <Typography.Text>{label}</Typography.Text>
                  <StakeholderStatus
                    style={{ padding: '0 8px', marginRight: -2 }}
                    status={selectedLeaderStatus as StakeholderStatusType}
                  />
                </Flex>
              )}
              options={populatedFGLOptions}
              loading={isFGLLoading}
              optionRender={(option) => (
                <Flex justify="space-between" align="center">
                  <Typography.Text>{option?.data?.label}</Typography.Text>
                  {!isFGLLoading && (
                    <StakeholderStatus
                      status={option.data?.status}
                      style={{ padding: '0 8px' }}
                    />
                  )}
                </Flex>
              )}
            />
          </Form.Item>
          <Form.Item
            name="juniorAgronomistEmail"
            label={t('common.salesAgronomistEmail')}
            required
            rules={[{ required: true }]}
            {...formProps}
          >
            <SelectComponent
              placeholder={t('addFgForm.selectEmail')}
              showSearch
              options={agronomistFilterOptions}
              loading={isAgronomistLoading}
              disabled={!(isLeaderActive && isPaddyBuyerActive)}
              hint={t('addFgForm.jrAgronomistHint')}
            />
          </Form.Item>
        </Card>

        <AddressForm form={form} type="farmerGroup" autoReset={false} />

        <Card>
          <Typography.Title level={5} style={{ marginBottom: '4px' }}>
            {t('addFgForm.estimatedStartDates')}
          </Typography.Title>
          <Typography.Text style={{ marginBottom: '24px', display: 'block' }}>
            {t('addFgForm.enterEstimatedStartDates')}
          </Typography.Text>
          <Form.Item
            name="firstEstimatedStartDate"
            label={t('addFgForm.firstEstimatedStartDate')}
            required
            dependencies={[
              'secondEstimatedStartDate',
              'thirdEstimatedStartDate',
            ]}
            rules={[{ required: true }, validateDatePicker]}
            {...formProps}
          >
            <DatePicker
              popupClassName="estimate-date-popup"
              format="DD MMMM"
              needConfirm
              showNow={false}
            />
          </Form.Item>
          <Form.Item
            name="secondEstimatedStartDate"
            label={t('addFgForm.secondEstimatedStartDate')}
            required
            dependencies={[
              'firstEstimatedStartDate',
              'thirdEstimatedStartDate',
            ]}
            rules={[{ required: true }, validateDatePicker]}
            {...formProps}
          >
            <DatePicker
              popupClassName="estimate-date-popup"
              format="DD MMMM"
              needConfirm
              showNow={false}
            />
          </Form.Item>
          <Form.Item
            name="thirdEstimatedStartDate"
            label={t('addFgForm.thirdEstimatedStartDate')}
            dependencies={[
              'firstEstimatedStartDate',
              'secondEstimatedStartDate',
            ]}
            rules={[validateDatePicker]}
            {...formProps}
          >
            <DatePicker
              popupClassName="estimate-date-popup"
              format="DD MMMM"
              needConfirm
              showNow={false}
            />
          </Form.Item>
        </Card>

        <Card>
          <Checkbox
            checked={isFullstackParticipant}
            onChange={() => {
              setIsFullstackParticipant(!isFullstackParticipant);
              setIsTouched(true);
            }}
          >
            {t('addFgForm.fullstackProgramAgreement')}
          </Checkbox>
        </Card>
      </FormContainer>

      <Modal
        title={t('addFgForm.createFarmerGroup')}
        open={isConfirmOpen}
        okText={t('addFgForm.submitFarmerGroup')}
        onOk={submitData}
        onCancel={() => setIsConfirmOpen(false)}
        confirmLoading={mutation.isPending}
        centered={isMobileScreenSize}
      >
        {t('addFgForm.submitFarmerGroupConfirm', { jrAgronomist })}
      </Modal>

      <Modal
        title={t('addFgForm.saveFarmerGroupChanges')}
        open={isConfirmEditOpen}
        okText={t('addFgForm.saveChanges')}
        onOk={submitData}
        onCancel={() => setIsConfirmEditOpen(false)}
        confirmLoading={mutation.isPending}
        centered={isMobileScreenSize}
      >
        {t('addFgForm.saveChangesConfirm', { jrAgronomist })}
      </Modal>
    </>
  );
};

export default FarmerGroupForm;
